import type { AccountProfile, AssistanceOverview, AccountSession, SecurityOverview, GdprRequest } from "./accountApi";
import type { Vehicle } from "../types";
import type { PrepView } from "../components/PreparationShop";

export const SUPPORT_PREVIEW_PARENT = "https://precisionautotechnik.synology.me:8443";

export type SupportPreviewHistory = {
  entries: Array<{ id: string; title: string; date: string; workshop?: string }>;
  details: Record<string, AssistanceOverview>;
};

export type SupportPreviewSnapshot = {
  account: AccountProfile;
  vehicles: Vehicle[];
  assistanceByVehicle: Record<string, AssistanceOverview>;
  historyByVehicle: Record<string, SupportPreviewHistory>;
  preparationByToken?: Record<string, PrepView>;
  sessions: AccountSession[];
  security: SecurityOverview;
  gdprRequests: GdprRequest[];
  updatedAt: string;
};

let snapshot: SupportPreviewSnapshot | null = null;

export function setSupportPreview(value: SupportPreviewSnapshot | null) {
  snapshot = value;
}

export function isSupportPreviewMessage(event: Pick<MessageEvent, "origin" | "source" | "data">, parent: Window) {
  return event.origin === SUPPORT_PREVIEW_PARENT &&
    event.source === parent &&
    event.data?.type === "precision-support-preview" &&
    Boolean(event.data.snapshot?.account?.id) &&
    Array.isArray(event.data.snapshot?.vehicles);
}

const json = (value: unknown, status = 200) => Response.json(value, { status });

export async function supportPreviewFetch(path: string, init: RequestInit = {}) {
  const method = String(init.method || "GET").toUpperCase();
  if (method !== "GET") return json({ error: "PREVIEW_READ_ONLY" }, 403);
  if (!snapshot) return json({ error: "PREVIEW_NOT_READY" }, 503);
  if (path === "/api/v1/session") return json({ authenticated: true, account: snapshot.account });
  if (path === "/api/v1/vehicles") return json({ vehicles: snapshot.vehicles });
  if (path === "/api/v1/preparation") {
    const token = new Headers(init.headers).get("X-Service-Token") || "";
    const preparation = snapshot.preparationByToken?.[token];
    return preparation ? json(preparation) : json({ error: "PREPARATION_NOT_FOUND" }, 404);
  }
  if (path === "/api/v1/shop/catalog/tvpf") {
    const base = String(import.meta.env.VITE_API_BASE_URL || "https://precisionautotechnik.synology.me:8443/precision-ev-api/index.php").replace(/\/$/, "");
    const url = /\/index\.php$/i.test(base) ? `${base}?path=${encodeURIComponent(path)}` : `${base}${path}`;
    return fetch(url, { method: "GET", cache: "no-store", credentials: "omit", signal: init.signal });
  }
  if (path === "/api/v1/public/inventory") {
    const base = String(import.meta.env.VITE_API_BASE_URL || "https://precisionautotechnik.synology.me:8443/precision-ev-api/index.php").replace(/\/$/, "");
    const url = /\/index\.php$/i.test(base) ? `${base}?path=${encodeURIComponent(path)}` : `${base}${path}`;
    return fetch(url, { method: "GET", cache: "no-store", credentials: "omit", signal: init.signal });
  }
  if (path === "/api/v1/security/sessions") return json({ sessions: snapshot.sessions || [] });
  if (path === "/api/v1/security") return json(snapshot.security || { events: [] });
  if (path === "/api/v1/privacy/exports") return json({ requests: snapshot.gdprRequests || [] });
  const assistance = path.match(/^\/api\/v1\/vehicles\/([^/]+)\/assistance$/);
  if (assistance) return json(snapshot.assistanceByVehicle?.[decodeURIComponent(assistance[1])] || { thread: null, messages: [], workshop: {}, dossier: null });
  const history = path.match(/^\/api\/v1\/vehicles\/([^/]+)\/assistance\/history(?:\/([^/]+))?$/);
  if (history) {
    const vehicleId = decodeURIComponent(history[1]);
    const data = snapshot.historyByVehicle?.[vehicleId] || { entries: [], details: {} };
    return history[2] ? json(data.details?.[decodeURIComponent(history[2])] || { error: "HISTORY_NOT_FOUND" }, data.details?.[decodeURIComponent(history[2])] ? 200 : 404) : json({ entries: data.entries || [] });
  }
  const drivers = path.match(/^\/api\/v1\/vehicles\/([^/]+)\/drivers$/);
  if (drivers) return json({ drivers: [], invites: [] });
  if (path === "/api/v1/shop/orders" || path === "/api/v1/shop/order-history") return json({ orders: [] });
  if (path === "/api/v1/shop/wallet") return json({ cards: [], balance: 0 });
  return json({ error: "PREVIEW_READ_ONLY" }, 403);
}
